import type Position from '@/data/position/model';
import {
  RIPPLING_POSITION_DELETE_ERROR_CODE,
  RIPPLING_POSITION_DELETE_PAY_RUN_ERROR_CODE,
} from '@/integrations/modules/rippling/consts';
import Monolith from '@/shared/api/monolith';
import { parseAPIError } from '@/shared/redux/helpers/parseApiError';
import { createWiwAsyncThunk } from '@/store';

type DeletePositionResponse = {
  success: boolean;
};

export const deletePosition = createWiwAsyncThunk<DeletePositionResponse, Position>(
  'position/delete',
  async (position, { rejectWithValue }) => {
    try {
      return (await Monolith.url(`/positions/${position.id}`).delete()) as DeletePositionResponse;
    } catch (error: any) {
      const parsed = parseAPIError(error);

      if (
        parsed.code === RIPPLING_POSITION_DELETE_PAY_RUN_ERROR_CODE ||
        parsed.code === RIPPLING_POSITION_DELETE_ERROR_CODE
      ) {
        return rejectWithValue({
          code: parsed.code,
          message: parsed.message,
        });
      }
      return rejectWithValue(parsed);
    }
  },
);

type BatchDeleteResult = {
  success?: boolean;
  id?: number;
  error?: string;
  code?: number;
};

type BatchDeleteRejectValue = {
  code: number;
  message: string;
  failedPositions: Position[];
  succeededPositions: Position[];
};

export const deletePositions = createWiwAsyncThunk<
  BatchDeleteResult[],
  Position[],
  { rejectValue: BatchDeleteRejectValue }
>('position/deleteMulti', async (positions, { rejectWithValue }) => {
  try {
    return await Monolith.url('/positions')
      .query({
        ids: positions.map(position => position.id),
      })
      .delete();
  } catch (error) {
    const resultError = parseDeletePositionsResponse(error, positions);
    return rejectWithValue(resultError);
  }
});

function parseDeletePositionsResponse(error: any, positions: Position[]): BatchDeleteRejectValue {
  const body: BatchDeleteResult[] | undefined = error?.json;

  if (!body || !Array.isArray(body)) {
    return {
      code: 0,
      message: 'Oops, something went wrong on our end. Try again in a few minutes.',
      failedPositions: [],
      succeededPositions: [],
    };
  }

  const positionById = new Map(positions.map(position => [position.id, position]));
  const failedPositions: Position[] = [];
  const succeededPositions: Position[] = [];
  let errorCode = 0;

  body.forEach(result => {
    const position = result.id ? positionById.get(result.id) : undefined;

    if (!position) {
      return;
    }
    if (result.error) {
      failedPositions.push(position);
    } else if (result.success) {
      succeededPositions.push(position);
    }
  });

  if (failedPositions.length > 0) {
    const hasRipplingPayRunFailure = body.some(r => r.error && r.code === RIPPLING_POSITION_DELETE_PAY_RUN_ERROR_CODE);
    const hasRipplingFailure = body.some(r => r.error && r.code === RIPPLING_POSITION_DELETE_ERROR_CODE);

    if (hasRipplingPayRunFailure) {
      errorCode = RIPPLING_POSITION_DELETE_PAY_RUN_ERROR_CODE;
    }
    if (hasRipplingFailure) {
      // General failure will have higher precedence
      errorCode = RIPPLING_POSITION_DELETE_ERROR_CODE;
    }
  }

  return {
    code: errorCode,
    message: 'Some positions could not be deleted.',
    failedPositions,
    succeededPositions,
  };
}
